import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { FaInstagram, FaFacebookF, FaWhatsapp } from 'react-icons/fa';
import styles from './SocialIcons.module.css';

const SocialIcons = ({
                         instagramUrl = 'https://www.instagram.com/giri.dresden/',
                         facebookUrl = 'https://www.facebook.com/',
                         whatsappNumber = '+4935130958928',
                         layout = 'horizontal',
                         theme = 'light',
                         showTooltip = true,
                         animationType = 'stagger',
                         enableHoverAnimation = true,
                         enableFloating = false,
                         enablePulse = false,
                         // New fixed props
                         fixed = false,
                         position = 'centerRight', // 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'centerLeft' | 'centerRight'
                     }) => {
    const containerRef = useRef(null);
    const iconsRef = useRef([]);

    const socialLinks = [
        {
            name: 'Instagram',
            icon: FaInstagram,
            url: instagramUrl,
            className: styles.instagram,
        },
        // {
        //     name: 'Facebook',
        //     icon: FaFacebookF,
        //     url: facebookUrl,
        //     className: styles.facebook,
        // },
        {
            name: 'WhatsApp',
            icon: FaWhatsapp,
            url: whatsappNumber,
            className: styles.whatsapp,
        },
    ];

    // Initial animation on mount
    useEffect(() => {
        const icons = iconsRef.current;

        gsap.set(icons, { opacity: 0 });

        switch (animationType) {
            case 'stagger':
                gsap.to(icons, {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    stagger: 0.15,
                    ease: 'back.out(1.7)',
                    from: { y: 30 },
                });
                break;

            case 'bounce':
                gsap.fromTo(
                    icons,
                    { opacity: 0, scale: 0 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.1,
                        ease: 'elastic.out(1, 0.5)',
                    }
                );
                break;

            case 'rotate':
                gsap.fromTo(
                    icons,
                    { opacity: 0, rotation: -180, scale: 0 },
                    {
                        opacity: 1,
                        rotation: 0,
                        scale: 1,
                        duration: 0.8,
                        stagger: 0.15,
                        ease: 'back.out(1.7)',
                    }
                );
                break;

            case 'scale':
                gsap.fromTo(
                    icons,
                    { opacity: 0, scale: 0.3 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.5,
                        stagger: 0.1,
                        ease: 'power2.out',
                    }
                );
                break;

            default:
                gsap.to(icons, { opacity: 1, duration: 0.5 });
        }
    }, [animationType]);

    // Hover animations
    const handleMouseEnter = (index) => {
        if (!enableHoverAnimation) return;

        const icon = iconsRef.current[index];

        gsap.to(icon, {
            scale: 1.2,
            duration: 0.3,
            ease: 'back.out(1.7)',
        });

        const innerIcon = icon.querySelector(`.${styles.icon}`);
        gsap.to(innerIcon, {
            rotation: 360,
            duration: 0.5,
            ease: 'power2.out',
        });
    };

    const handleMouseLeave = (index) => {
        if (!enableHoverAnimation) return;

        const icon = iconsRef.current[index];

        gsap.to(icon, {
            scale: 1,
            duration: 0.3,
            ease: 'power2.out',
        });

        const innerIcon = icon.querySelector(`.${styles.icon}`);
        gsap.to(innerIcon, {
            rotation: 0,
            duration: 0.3,
            ease: 'power2.out',
        });
    };

    // Click animation
    const handleClick = (index) => {
        const icon = iconsRef.current[index];

        gsap.to(icon, {
            scale: 0.9,
            duration: 0.1,
            yoyo: true,
            repeat: 1,
            ease: 'power2.inOut',
        });
    };

    // Build container classes
    const containerClasses = [
        styles.container,
        layout === 'vertical' ? styles.vertical : '',
        theme === 'dark' ? styles.dark : '',
        fixed ? styles.fixed : '',
        fixed ? styles[position] : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div ref={containerRef} className={containerClasses}>
            {socialLinks.map((social, index) => {
                const IconComponent = social.icon;

                const wrapperClasses = [
                    styles.iconWrapper,
                    social.className,
                    enableFloating ? styles.floating : '',
                    enablePulse ? styles.pulse : '',
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.link}
                        aria-label={social.name}
                    >
                        <div
                            ref={(el) => (iconsRef.current[index] = el)}
                            className={wrapperClasses}
                            onMouseEnter={() => handleMouseEnter(index)}
                            onMouseLeave={() => handleMouseLeave(index)}
                            onClick={() => handleClick(index)}
                            style={{ animationDelay: `${index * 0.2}s` }}
                        >
                            <IconComponent className={styles.icon} />
                            {showTooltip && (
                                <span className={styles.tooltip}>{social.name}</span>
                            )}
                        </div>
                    </a>
                );
            })}
        </div>
    );
};

export default SocialIcons;